/**
 * Het tabblad Niveauteksten (stap 18): elke standaardtekst op elk niveau,
 * gemaakt en goedgekeurd door de beheerder, voordat een leerkracht hem kan
 * gebruiken (backend: services/niveauteksten.py).
 *
 * De lijst toont per tekst een blokje per niveau, met de kleur van wat er
 * is: niets, bezig, een concept, mislukt of goedgekeurd. "Maak ontbrekende
 * niveaus" laat de server ze op de achtergrond maken, één na het andere (een
 * tot drie minuten per niveau); zolang er iets bezig is, vraagt dit tabblad
 * het overzicht elke vijf seconden opnieuw op.
 *
 * Klik op een blokje en het venster opent op dat niveau: de tekst, de
 * lengte die bij het niveau hoort, BILT en REF tegen de band van het
 * niveau, en de knoppen bewerken, opnieuw maken, goedkeuren en concept
 * weggooien. Een goedgekeurde versie blijft gelden terwijl er een nieuw
 * concept van gemaakt wordt; pas goedkeuren vervangt hem.
 *
 * De lengte per niveau stelt de beheerder in het tabblad Tekstlengte in.
 * Verandert hij die, dan blijft een goedgekeurde versie gelden met haar eigen
 * lengte; haar blokje krijgt dan een stippelrand, en "Opnieuw op de nieuwe
 * lengte" laat die niveaus opnieuw maken.
 *
 * Stap 19 (backend: services/kerngedachten.py en werkwijzen.py):
 * - bovenaan de werkwijze bij het maken: van twee kanten, stapsgewijs omlaag,
 *   of elk niveau los. Ze geldt voor wat daarna gestart wordt;
 * - per tekst de knop Kerngedachten: de lijst die elk niveau deelt, met bij
 *   elke kerngedachte vanaf welk niveau hij erin staat (eerst de kern, dan
 *   de voorbeelden, dan de uitwerking, dan de details). De beheerder kan de
 *   tekst en de soort veranderen, kerngedachten weghalen, toevoegen en
 *   verschuiven, of ze opnieuw laten halen; "vanaf" rekent meteen mee;
 * - in het niveauvenster hoe de versie gemaakt is: de werkwijze, de niveaus
 *   ernaast als vertrekpunt, het aantal kerngedachten, BILT per poging.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Check, Edit3, Eye, Lightbulb, Plus, RefreshCw, Save, Sparkles, Trash2, X } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { Venster } from '../../../components/Venster';
import { Draaier, Laden } from '../../../components/Laden';
import { zonderKopjes } from '../../../components/OpgemaakteTekst';
import { GemarkeerdeTekst, heeftMarkeringen } from '../../../components/Verhaalopzet';
import { TekstMaten } from '../../../components/TekstMaten';
import { genreVan } from '../../../keuzelijsten';

const ELKE_VIJF_SECONDEN = 5000;

export const STATUS = {
  ontbreekt: { label: 'nog niet gemaakt', achter: 'var(--vlak)', rand: 'var(--lijn-3)', tekst: 'var(--tekst-3)' },
  bezig: { label: 'wordt gemaakt', achter: 'var(--blauw-vlak)', rand: 'var(--blauw)', tekst: 'var(--blauw)' },
  concept: { label: 'concept: nog goedkeuren', achter: 'var(--waarschuw-vlak)', rand: 'var(--waarschuw)', tekst: 'var(--waarschuw-tekst)' },
  mislukt: { label: 'mislukt', achter: 'var(--fout-vlak)', rand: 'var(--fout)', tekst: 'var(--fout)' },
  goedgekeurd: { label: 'goedgekeurd', achter: 'var(--goed-vlak)', rand: 'var(--goed)', tekst: 'var(--goed-tekst)' },
};

/** Valt een versie buiten de band van haar niveau (BILT, de zinslengte, of REF bij de 2F-niveaus)? */
const buitenDeBand = (m) => Boolean(m && (m.bilt_binnen === false || m.zinslengte_binnen === false
  || (m.ref_stuurt_bij && m.ref_binnen === false)));

const tijd = (iso) => (iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '');
const getal = (x) => (typeof x === 'number' ? x.toFixed(1).replace('.', ',') : '?');

// De rangorde bij het inkorten (zoals services/kerngedachten.py): eerst de kern, dan de voorbeelden, dan de uitwerking, dan de details.
const SOORTEN = ['KERN', 'VOORBEELD', 'UITWERKING', 'DETAIL'];
const SOORTNAAM = { KERN: 'Kern', VOORBEELD: 'Voorbeeld', UITWERKING: 'Uitwerking', DETAIL: 'Detail' };

/** Per kerngedachte (op plek in de lijst) het laagste niveau waarop hij in de tekst komt; null: op geen enkel. */
export function vanafNiveaus(lijst, perNiveau) {
  const niveaus = Object.keys(perNiveau);
  const gevuld = lijst.map((k, i) => ({ i, rang: SOORTEN.indexOf(k.soort) })).filter(k => lijst[k.i].tekst.trim());
  gevuld.sort((a, b) => (a.rang - b.rang) || (a.i - b.i));
  const uit = lijst.map(() => null);
  gevuld.forEach((k, plek) => { uit[k.i] = niveaus.find(n => plek < perNiveau[n]) || null; });
  return uit;
}

/** Wat er op één niveau is, in woorden (voor de titel van het blokje en de statusregel). */
export function uitleg(n) {
  const delen = [STATUS[n.status]?.label || n.status];
  if (n.goedgekeurd && n.status !== 'goedgekeurd') delen.push('de goedgekeurde versie geldt nog');
  if (n.andere_lengte) {
    delen.push(`gemaakt met ${n.concept ? n.concept_lengte : n.live_lengte} woorden; nu ingesteld: ${n.lengte}`);
  }
  if (n.fout) delen.push(n.fout);
  return delen.join('; ');
}

function Blokje({ n, onKlik, gekozen }) {
  const s = STATUS[n.status] || STATUS.ontbreekt;
  return (
    <button type="button" onClick={onKlik} title={`${n.niveau} (${n.lengte} woorden): ${uitleg(n)}`}
      data-testid={`blokje-${n.niveau}`} data-status={n.status} data-andere-lengte={n.andere_lengte ? 'ja' : undefined}
      style={{
        padding: '3px 6px', fontSize: 11, borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
        background: s.achter, color: s.tekst, border: `${gekozen ? 2 : 1}px ${n.andere_lengte ? 'dashed' : 'solid'} ${s.rand}`,
        boxShadow: n.goedgekeurd && n.status !== 'goedgekeurd' ? `inset 0 -3px 0 var(--goed)` : 'none',
      }}>
      {n.status === 'bezig' && <Draaier grootte={10} />} {n.niveau}{n.andere_lengte && ' ≠'}
    </button>
  );
}

export function Niveauteksten({ actief, meldFout }) {
  const [teksten, setTeksten] = useState([]);
  const [laden, setLaden] = useState(false);
  const [bezigMet, setBezigMet] = useState({});       // per tekst: wordt het maken net gestart?
  const [bericht, setBericht] = useState('');
  const [open, setOpen] = useState(null);             // { tekstId, niveau } in het venster
  const [werkwijze, setWerkwijze] = useState(null);   // { werkwijze, werkwijzen: [{ id, naam, uitleg }] }
  const [kerngedachtenVan, setKerngedachtenVan] = useState(null);   // tekst-id in het venster Kerngedachten

  const herlaad = async () => {
    const data = await api.haalNiveauteksten();
    setTeksten(data);
    return data;
  };

  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    herlaad().catch(err => meldFout(err.message)).finally(() => setLaden(false));
    api.haalWerkwijze().then(setWerkwijze).catch(err => meldFout(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  // Zolang er iets gemaakt wordt: elke vijf seconden het overzicht opnieuw.
  const ergensBezig = teksten.some(t => t.niveaus.some(n => n.status === 'bezig'));
  useEffect(() => {
    if (!actief || !ergensBezig) return undefined;
    const klok = setInterval(() => herlaad().catch(() => {}), ELKE_VIJF_SECONDEN);
    return () => clearInterval(klok);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- herlaad verandert niet van betekenis
  }, [actief, ergensBezig]);

  const maakOntbrekende = async (tekst) => {
    setBezigMet(prev => ({ ...prev, [tekst.id]: true }));
    setBericht('');
    try {
      const uit = await api.maakNiveauteksten(tekst.id);
      setBericht(uit.gestart.length
        ? `'${tekst.titel}': ${uit.gestart.length} niveau(s) worden nu gemaakt, één na het andere. Dat duurt een tot drie minuten per niveau.`
        : `'${tekst.titel}': elk niveau heeft al een concept of een goedgekeurde versie.`);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBezigMet(prev => ({ ...prev, [tekst.id]: false }));
    }
  };

  const maakOpNieuweLengte = async (tekst, niveaus) => {
    if (!window.confirm(`${niveaus.join(', ')} van '${tekst.titel}' opnieuw laten maken op de lengte die nu is ingesteld? `
      + 'De goedgekeurde versies blijven gelden tot je de nieuwe goedkeurt.')) return;
    setBezigMet(prev => ({ ...prev, [tekst.id]: true }));
    setBericht('');
    try {
      const uit = await api.maakNiveauteksten(tekst.id, niveaus);
      setBericht(`'${tekst.titel}': ${uit.gestart.length} niveau(s) worden opnieuw gemaakt, op de nieuwe lengte.`);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBezigMet(prev => ({ ...prev, [tekst.id]: false }));
    }
  };

  const kiesWerkwijze = async (id) => {
    try {
      setWerkwijze(await api.kiesWerkwijze(id));
    } catch (err) {
      meldFout(err.message);
    }
  };

  const keurAllesGoed = async (tekst) => {
    const concepten = tekst.niveaus.filter(n => n.concept && n.status !== 'bezig');
    const buiten = concepten.filter(n => buitenDeBand(n.maten)).map(n => n.niveau);
    const afwijkend = concepten.filter(n => n.inhoud_wijkt_af).map(n => n.niveau);
    const vraag = `Alle ${concepten.length} concepten van '${tekst.titel}' goedkeuren (${concepten.map(n => n.niveau).join(', ')})? Bekijk ze eerst.`
      + (buiten.length ? `\n\nLet op: ${buiten.join(', ')} ${buiten.length === 1 ? 'valt' : 'vallen'} buiten de band van het niveau (BILT, REF of zinnen).` : '')
      + (afwijkend.length ? `\n\nBij ${afwijkend.join(', ')} wijkt de inhoud af van de kerngedachten (zie Hoe gemaakt).` : '');
    if (!window.confirm(vraag)) return;
    try {
      const uit = await api.keurNiveautekstGoed(tekst.id);
      setBericht(`'${tekst.titel}': ${uit.message}`);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    }
  };

  if (laden && teksten.length === 0) return <Laden tekst="Niveauteksten laden..." />;

  const lengtes = teksten[0]?.niveaus || [];

  return (
    <div className="card">
      <h2 className="mb-8">Niveauteksten</h2>
      <p className="text-muted mb-16" style={{ fontSize: 14 }}>
        Elke tekst wordt op elk niveau gemaakt, met de lengte van dat niveau, en door jou goedgekeurd. Pas als alle
        niveaus goedgekeurd zijn, kan een leerkracht de tekst in een les gebruiken.
        {lengtes.length > 0 && <> Lengtes: {lengtes.map(n => `${n.niveau} ${n.lengte}`).join(' · ')} woorden (in te stellen in het tabblad Tekstlengte).</>}
      </p>

      <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap', fontSize: 12 }}>
        {Object.entries(STATUS).map(([sleutel, s]) => (
          <span key={sleutel} style={{ padding: '2px 6px', borderRadius: 4, background: s.achter, color: s.tekst, border: `1px solid ${s.rand}` }}>
            {s.label}
          </span>
        ))}
      </div>

      {werkwijze && (
        <div className="card mb-16" style={{ background: 'var(--vlak)', padding: '10px 14px' }} data-testid="werkwijze">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 14 }}>
            <strong>Werkwijze bij het maken:</strong>
            <select className="input" style={{ width: 'auto', padding: '4px 8px', fontSize: 13 }} value={werkwijze.werkwijze}
              onChange={(e) => kiesWerkwijze(e.target.value)} aria-label="Werkwijze">
              {werkwijze.werkwijzen.map(w => <option key={w.id} value={w.id}>{w.naam}</option>)}
            </select>
          </label>
          <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
            {werkwijze.werkwijzen.find(w => w.id === werkwijze.werkwijze)?.uitleg} Elk niveau krijgt zijn deel van dezelfde
            kerngedachten; het vertrekpunt is er voor de taal, niet voor de inhoud. Geldt voor wat je hierna laat maken.
            Een verhaal of dakpanset in deelteksten gaat anders: daar houden de bouwstenen de niveaus gelijk.
          </div>
        </div>
      )}

      {bericht && <div className="alert alert-success mb-16">{bericht}</div>}

      <table>
        <thead>
          <tr>
            <th>Tekst</th>
            <th>Niveaus</th>
            <th>Klaar voor de klas</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {teksten.map(t => {
            const concepten = t.niveaus.filter(n => n.concept && n.status !== 'bezig').length;
            const nogTeMaken = t.niveaus.some(n => !n.goedgekeurd && !n.concept && n.status !== 'bezig');
            const andereLengte = t.niveaus.filter(n => n.andere_lengte && n.status !== 'bezig').map(n => n.niveau);
            return (
              <tr key={t.id} data-testid={`niveautekst-${t.id}`}>
                <td>
                  {t.titel}
                  {genreVan(t) && (
                    <span className="badge" style={{ marginLeft: 6, fontSize: 11, color: 'var(--wit)', background: genreVan(t).kleur }}>
                      {genreVan(t).label}
                    </span>
                  )}
                  {t.lessen > 0 && <div className="text-muted" style={{ fontSize: 12 }}>in {t.lessen} les(sen)</div>}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {t.niveaus.map(n => <Blokje key={n.niveau} n={n} onKlik={() => setOpen({ tekstId: t.id, niveau: n.niveau })} />)}
                  </div>
                </td>
                <td>
                  {t.klaar
                    ? <span className="badge badge-success">Klaar</span>
                    : <span className="badge badge-warning">{t.niveaus.filter(n => n.goedgekeurd).length} van {t.niveaus.length}</span>}
                </td>
                <td>
                  <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
                    {nogTeMaken && (
                      <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: 13 }}
                        onClick={() => maakOntbrekende(t)} disabled={bezigMet[t.id]}>
                        {bezigMet[t.id] ? <Draaier /> : <Sparkles size={14} />} Maak ontbrekende niveaus
                      </button>
                    )}
                    {andereLengte.length > 0 && (
                      <button className="btn btn-warning" style={{ padding: '4px 8px', fontSize: 13 }}
                        onClick={() => maakOpNieuweLengte(t, andereLengte)} disabled={bezigMet[t.id]}
                        title={`Met een andere lengte gemaakt dan nu is ingesteld: ${andereLengte.join(', ')}`}>
                        <RefreshCw size={14} /> Opnieuw op de nieuwe lengte ({andereLengte.length})
                      </button>
                    )}
                    {concepten > 0 && (
                      <button className="btn btn-success" style={{ padding: '4px 8px', fontSize: 13 }} onClick={() => keurAllesGoed(t)}>
                        <Check size={14} /> Keur {concepten} concept(en) goed
                      </button>
                    )}
                    {!t.verhaal && (
                      <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 13 }}
                        onClick={() => setKerngedachtenVan(t.id)}
                        title="De kerngedachten die elk niveau deelt, en vanaf welk niveau elk erin staat">
                        <Lightbulb size={14} /> Kerngedachten{t.kerngedachten ? ` (${t.kerngedachten})` : ''}
                      </button>
                    )}
                    <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 13 }}
                      onClick={() => setOpen({ tekstId: t.id, niveau: t.niveaus[0].niveau })}>
                      <Eye size={14} /> Bekijk
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {open && (
        <NiveauVenster
          tekst={teksten.find(t => t.id === open.tekstId)}
          niveau={open.niveau}
          kiesNiveau={(niveau) => setOpen({ ...open, niveau })}
          onSluit={() => setOpen(null)}
          herlaad={herlaad}
          meldFout={meldFout}
        />
      )}
      {kerngedachtenVan && (
        <KerngedachtenVenster
          tekst={teksten.find(t => t.id === kerngedachtenVan)}
          onSluit={() => setKerngedachtenVan(null)}
          herlaad={herlaad}
          meldFout={meldFout}
        />
      )}
    </div>
  );
}

/** De kerngedachten van één tekst: bekijken, met vanaf welk niveau elk erin staat, en bewerken. */
function KerngedachtenVenster({ tekst, onSluit, herlaad, meldFout }) {
  const [gegevens, setGegevens] = useState(null);
  const [lijst, setLijst] = useState([]);              // [{ tekst, soort }], zoals hij in de velden staat
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState('');
  const ergensBezig = tekst?.niveaus.some(n => n.status === 'bezig');

  const zet = (data) => {
    setGegevens(data);
    setLijst(data.lijst.map(k => ({ tekst: k.tekst, soort: k.soort })));
  };

  useEffect(() => {
    if (!tekst) return;
    api.haalKerngedachten(tekst.id).then(zet).catch(err => meldFout(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bij een andere tekst
  }, [tekst?.id]);

  if (!tekst) return null;

  const bewaard = gegevens ? JSON.stringify(gegevens.lijst.map(k => ({ tekst: k.tekst, soort: k.soort }))) : '[]';
  const ingevuld = lijst.filter(k => k.tekst.trim()).map(k => ({ tekst: k.tekst.trim().replace(/\s+/g, ' '), soort: k.soort }));
  const veranderd = JSON.stringify(ingevuld) !== bewaard;
  const niveaus = gegevens ? Object.keys(gegevens.per_niveau) : [];
  const vanaf = gegevens ? vanafNiveaus(lijst, gegevens.per_niveau) : [];
  const gevuld = ingevuld.length;

  const wijzig = (i, veld, waarde) => setLijst(prev => prev.map((k, j) => (j === i ? { ...k, [veld]: waarde } : k)));
  const schuif = (i, stap) => setLijst(prev => {
    const nieuw = [...prev];
    [nieuw[i], nieuw[i + stap]] = [nieuw[i + stap], nieuw[i]];
    return nieuw;
  });

  const doe = async (actie, gelukt) => {
    setBezig(true);
    setMelding('');
    try {
      zet(await actie());
      setMelding(gelukt);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  // met de versie die je bewerkte: is die intussen veranderd, dan bewaart de server niets (en zegt dat)
  const bewaar = () => doe(() => api.bewaarKerngedachten(tekst.id, ingevuld, gegevens.versie),
    'Bewaard. Wat je hierna laat maken, krijgt deze kerngedachten; wat al gemaakt is, blijft.');

  const haalOpnieuw = () => {
    if (gegevens?.gehaald && !window.confirm('De kerngedachten opnieuw uit de standaardtekst laten halen? '
      + 'De lijst die er nu is, met je bewerkingen, vervalt.')) return;
    doe(() => api.haalKerngedachtenOpnieuw(tekst.id), 'Opnieuw gehaald uit de standaardtekst.');
  };

  const sluit = () => {
    if (veranderd && !window.confirm('Je bewerkingen zijn nog niet bewaard. Toch sluiten?')) return;
    onSluit();
  };

  return (
    <Venster breed={1100} vanBoven>
      <div className="flex-between mb-8">
        <h2 style={{ margin: 0 }}>{tekst.titel}: kerngedachten</h2>
        <button className="btn btn-secondary" onClick={sluit} disabled={bezig}><X size={16} /> Sluiten</button>
      </div>
      <p className="text-muted mb-12" style={{ fontSize: 14 }}>
        Elk niveau krijgt een deel van deze kerngedachten, naar zijn lengte: eerst de kern, dan de voorbeelden, dan de
        uitwerking, dan de details, en binnen een soort de volgorde van de tekst. Wat op een niveau staat, staat dus ook
        op elk niveau erboven. Een voorbeeld gaat vóór een uitwerking: zo blijft de tekst voor jonge lezers concreet.
        Wat je bewaart, geldt voor wat je hierna laat maken; wat al gemaakt is, blijft.
      </p>

      {melding && <div className="alert alert-success mb-12">{melding}</div>}
      {ergensBezig && (
        <div className="alert alert-info mb-12">Er wordt nu een niveau van deze tekst gemaakt; bewerken kan als dat klaar is.</div>
      )}

      {!gegevens ? <Laden tekst="Kerngedachten laden..." /> : (
        <>
          {!gegevens.gehaald && (
            <p className="mb-12" style={{ fontSize: 14 }}>
              De kerngedachten zijn nog niet gehaald. Dat gebeurt vanzelf bij het eerste niveau dat je laat maken, of nu met
              "Uit de standaardtekst halen". Je kunt ze ook zelf invullen.
            </p>
          )}
          {lijst.length > 0 && (
            <table data-testid="kerngedachten">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>#</th>
                  <th style={{ width: 130 }}>Soort</th>
                  <th>Kerngedachte</th>
                  <th style={{ width: 90 }} title="Het laagste niveau waarop deze kerngedachte in de tekst komt">Vanaf</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {lijst.map((k, i) => {
                  const kernTeHoog = k.soort === 'KERN' && k.tekst.trim() && vanaf[i] !== niveaus[0];
                  return (
                    <tr key={i} data-testid={`kerngedachte-${i + 1}`}>
                      <td className="text-muted">{i + 1}</td>
                      <td>
                        <select className="input" style={{ padding: '4px 6px', fontSize: 13 }} value={k.soort}
                          aria-label={`Soort ${i + 1}`} onChange={(e) => wijzig(i, 'soort', e.target.value)} disabled={bezig}>
                          {SOORTEN.map(s => <option key={s} value={s}>{SOORTNAAM[s]}</option>)}
                        </select>
                      </td>
                      <td>
                        <input className="input" style={{ padding: '4px 8px', fontSize: 13 }} value={k.tekst}
                          aria-label={`Kerngedachte ${i + 1}`} maxLength={400} onChange={(e) => wijzig(i, 'tekst', e.target.value)} disabled={bezig} />
                      </td>
                      <td data-testid={`vanaf-${i + 1}`}>
                        {!k.tekst.trim() ? <span className="text-muted">-</span>
                          : vanaf[i] ? (
                            <span className={`badge ${kernTeHoog ? 'badge-warning' : 'badge-primary'}`} style={{ fontSize: 11, padding: '2px 8px' }}
                              title={kernTeHoog ? `Deze kern past pas vanaf ${vanaf[i]}: er zijn meer kernen dan op ${niveaus[0]} passen` : undefined}>
                              {vanaf[i]}
                            </span>
                          ) : <span className="text-muted" title="Past op geen enkel niveau">geen</span>}
                      </td>
                      <td>
                        <div className="flex gap-4">
                          <button type="button" className="btn btn-secondary" style={{ padding: '2px 6px' }} disabled={bezig || i === 0}
                            onClick={() => schuif(i, -1)} aria-label={`Kerngedachte ${i + 1} omhoog`}><ArrowUp size={12} /></button>
                          <button type="button" className="btn btn-secondary" style={{ padding: '2px 6px' }}
                            disabled={bezig || i === lijst.length - 1}
                            onClick={() => schuif(i, 1)} aria-label={`Kerngedachte ${i + 1} omlaag`}><ArrowDown size={12} /></button>
                          <button type="button" className="btn btn-danger" style={{ padding: '2px 6px' }} disabled={bezig}
                            onClick={() => setLijst(prev => prev.filter((_, j) => j !== i))}
                            aria-label={`Kerngedachte ${i + 1} weghalen`}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p className="text-muted mt-8" style={{ fontSize: 12 }} data-testid="per-niveau">
            Per niveau (naar de lengte): {niveaus.map(n => `${n} ${gegevens.per_niveau[n]}`).join(' · ')}.
            {gegevens.versie && <> Versie {gegevens.versie}{gegevens.bewerkt_op
              ? `, bewerkt ${tijd(gegevens.bewerkt_op)}${gegevens.bewerkt_door ? ` door ${gegevens.bewerkt_door}` : ''}`
              : gegevens.gehaald_op ? `, gehaald ${tijd(gegevens.gehaald_op)}` : ''}.</>}
          </p>
          <div className="flex gap-8 mt-12" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-success" onClick={bewaar} disabled={bezig || ergensBezig || !veranderd || gevuld === 0}>
              {bezig ? <Draaier /> : <Save size={16} />} Bewaren
            </button>
            <button className="btn btn-secondary" disabled={bezig || lijst.length >= gegevens.maximum}
              onClick={() => setLijst(prev => [...prev, { tekst: '', soort: 'UITWERKING' }])}>
              <Plus size={16} /> Kerngedachte toevoegen
            </button>
            <button className="btn btn-warning" onClick={haalOpnieuw} disabled={bezig || ergensBezig}>
              {bezig ? <Draaier /> : <RefreshCw size={16} />} {gegevens.gehaald ? 'Opnieuw uit de standaardtekst halen' : 'Uit de standaardtekst halen'}
            </button>
          </div>
        </>
      )}
    </Venster>
  );
}

/** Hoe een versie gemaakt is (stap 19): de werkwijze, de vertrekpunten, de kerngedachten, BILT per poging. */
function Verloop({ verloop }) {
  if (verloop.verhaal) {
    return (
      <div data-testid="verloop" className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
        Gemaakt uit de 3F-deelteksten, met de bouwstenen{verloop.met_de_hand && '; daarna met de hand bewerkt'}.
      </div>
    );
  }
  if (!verloop.werkwijze) {
    // een versie van vóór stap 19, met de hand bewerkt: van het maken is niets bewaard
    return verloop.met_de_hand
      ? <div data-testid="verloop" className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>Met de hand bewerkt.</div>
      : null;
  }
  const punten = verloop.vertrekpunten || [];
  const pogingen = verloop.pogingen || [];
  const vanuit = punten.length
    ? `vanuit ${punten.map(p => `${p.niveau} (${p.welke === 'concept' ? 'concept' : 'goedgekeurd'}${
      p.afstand > 1 ? ', twee niveaus ver' : ''}, BILT ${getal(p.bilt)})`).join(' en ')}`
    : 'zonder vertrekpunt';
  return (
    <div data-testid="verloop" style={{ fontSize: 13, marginTop: 4 }}>
      <strong>Hoe gemaakt:</strong> {verloop.werkwijze_naam || verloop.werkwijze}, {vanuit}
      {verloop.kerngedachten && ` · ${verloop.kerngedachten.length} kerngedachten`}
      {pogingen.length > 0 && ` · BILT per poging${verloop.oude_formule ? ' (vorige BILT-formule)' : ''}: ${pogingen.map(p => getal(p.bilt)).join(' → ')}`}
      {pogingen.some(p => typeof p.zinslengte === 'number') && ` · woorden per zin: ${pogingen.map(p => getal(p.zinslengte)).join(' → ')}`}
      {verloop.bijgestuurd > 0 && ` (${verloop.bijgestuurd} keer bijgestuurd${
        verloop.gekozen !== pogingen.length - 1 ? `; poging ${verloop.gekozen + 1} telt` : ''})`}
      {verloop.met_de_hand && ' · daarna met de hand bewerkt'}
      {verloop.andere_kerngedachten && (
        <span style={{ color: 'var(--waarschuw-tekst)' }}> · gemaakt met een eerdere lijst kerngedachten</span>
      )}
      <Inhoud inhoud={verloop.inhoud} metDeHand={verloop.met_de_hand} />
    </div>
  );
}

/** Wat het nakijken van de inhoud tegen de kerngedachten vond (stap 19d); niets bij een versie van daarvoor. */
function Inhoud({ inhoud, metDeHand }) {
  if (!inhoud) return null;
  if (!inhoud.nagekeken) return <span data-testid="inhoud"> · de inhoud kon niet nagekeken worden</span>;
  const ontbreekt = inhoud.ontbreekt || [];
  const toegevoegd = inhoud.toegevoegd || [];
  const na = inhoud.bijgestuurd ? ', na één keer bijsturen' : '';
  if (!ontbreekt.length && !toegevoegd.length) {
    return <span data-testid="inhoud"> · de inhoud volgt de kerngedachten{na}</span>;
  }
  return (
    <div data-testid="inhoud" style={{ color: 'var(--waarschuw-tekst)', marginTop: 4 }}>
      <strong>De inhoud wijkt af van de kerngedachten</strong>{na}{metDeHand && ' (nagekeken vóór het bewerken met de hand)'}:
      {ontbreekt.length > 0 && <div>Ontbreekt: {ontbreekt.join('; ')}</div>}
      {toegevoegd.length > 0 && <div>Niet uit de kerngedachten: {toegevoegd.map(z => `‘${z}’`).join('; ')}</div>}
    </div>
  );
}

/** Eén tekst op één niveau: bekijken, bewerken, opnieuw maken, goedkeuren. */
function NiveauVenster({ tekst, niveau, kiesNiveau, onSluit, herlaad, meldFout }) {
  const [versie, setVersie] = useState(null);
  const [welke, setWelke] = useState(null);           // concept of goedgekeurd; leeg: wat de server kiest
  const [laden, setLaden] = useState(true);
  const [bewerken, setBewerken] = useState(false);
  const [alineas, setAlineas] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState('');
  const [bouwstenen, setBouwstenen] = useState(false);
  const status = tekst?.niveaus.find(n => n.niveau === niveau);
  const vorigeStatus = useRef(status?.status);
  const laatsteVraag = useRef(0);   // komt een oud antwoord (van een ander niveau) later binnen, dan telt het niet

  const laad = async (keuze = welke) => {
    const vraag = ++laatsteVraag.current;
    setLaden(true);
    try {
      const data = await api.haalNiveauversie(tekst.id, niveau, keuze);
      if (vraag === laatsteVraag.current) setVersie(data);
    } catch (err) {
      if (vraag === laatsteVraag.current) meldFout(err.message);
    } finally {
      if (vraag === laatsteVraag.current) setLaden(false);
    }
  };

  useEffect(() => {
    setWelke(null);
    setBewerken(false);
    setMelding('');
    laad(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bij een ander niveau
  }, [tekst?.id, niveau]);

  // Was dit niveau bezig en is het nu klaar (het overzicht wordt elke vijf seconden opgehaald), dan het nieuwe concept tonen.
  useEffect(() => {
    if (vorigeStatus.current === 'bezig' && status?.status !== 'bezig') {
      setWelke(null);
      laad(null);
    }
    vorigeStatus.current = status?.status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status]);

  if (!tekst || !status) return null;

  const doe = async (actie, gelukt) => {
    setBezig(true);
    setMelding('');
    try {
      const uit = await actie();
      await herlaad();
      if (gelukt) setMelding(gelukt(uit));
      return uit;
    } catch (err) {
      meldFout(err.message);
      return null;
    } finally {
      setBezig(false);
    }
  };

  const maakOpnieuw = () => {
    const tekstje = status.goedgekeurd
      ? `Een nieuw concept op ${niveau} laten maken? De goedgekeurde versie blijft gelden tot je het nieuwe concept goedkeurt.`
      : `De tekst op ${niveau} (opnieuw) laten maken?`;
    if (!window.confirm(tekstje)) return;
    doe(() => api.maakNiveauteksten(tekst.id, [niveau]),
      () => `De tekst op ${niveau} wordt gemaakt. Dat duurt een tot drie minuten; dit venster werkt zichzelf bij.`);
  };

  const keurGoed = async () => {
    // de maten van het concept, ook als de goedgekeurde versie op het scherm staat
    let concept = versie?.welke === 'concept' ? versie : null;
    if (!concept) {
      try {
        concept = await api.haalNiveauversie(tekst.id, niveau, 'concept');
      } catch (err) {
        meldFout(err.message);
        return;
      }
    }
    const bezwaren = [
      buitenDeBand(concept?.maten) && 'Het concept valt buiten de band van dit niveau (zie BILT, REF en zinnen).',
      concept?.verloop?.inhoud_wijkt_af && 'De inhoud wijkt af van de kerngedachten (zie Hoe gemaakt).',
    ].filter(Boolean);
    if (bezwaren.length && !window.confirm(`${bezwaren.join(' ')} Toch goedkeuren?`)) return;
    const uit = await doe(() => api.keurNiveautekstGoed(tekst.id, niveau), (u) => u.message);
    if (uit) {
      setWelke(null);
      laad(null);
    }
  };

  const gooiWeg = async () => {
    if (!window.confirm(`Het concept op ${niveau} weggooien?`)) return;
    const uit = await doe(() => api.gooiConceptWeg(tekst.id, niveau), () => 'Het concept is weg.');
    if (uit) {
      setWelke(null);
      laad(null);
    }
  };

  const beginBewerken = () => {
    if (versie.welke === 'goedgekeurd' && versie.concept
      && !window.confirm('Er ligt al een concept. Bewerk je de goedgekeurde versie, dan vervangt die bewerking het concept. Doorgaan?')) return;
    setAlineas(versie.paragraphs.map(p => ({ id: p.id, inhoud: zonderKopjes(p.inhoud) })));
    setBewerken(true);
  };

  const bewaar = async () => {
    // Bewerkte hij wat hij als goedgekeurde versie zag, dan begint het concept bij die versie (niet bij een ander concept).
    const uit = await doe(() => api.bewaarConcept(tekst.id, niveau, alineas, versie.welke === 'goedgekeurd'),
      () => 'Bewaard als concept. Keur het goed om het de leerlingen te laten lezen.');
    if (uit) {
      setBewerken(false);
      setWelke('concept');
      setVersie(uit);
    }
  };

  const isBezig = status.status === 'bezig';
  const s = STATUS[status.status] || STATUS.ontbreekt;

  return (
    <Venster breed={1100} vanBoven>
      <div className="flex-between mb-16">
        <h2 style={{ margin: 0 }}>{tekst.titel}: {niveau}</h2>
        <button className="btn btn-secondary" onClick={onSluit} disabled={bewerken && bezig}><X size={16} /> Sluiten</button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
        {tekst.niveaus.map(n => (
          <Blokje key={n.niveau} n={n} gekozen={n.niveau === niveau}
            onKlik={() => { if (!bewerken) kiesNiveau(n.niveau); }} />
        ))}
      </div>

      <div className="card mb-12" style={{ background: s.achter, padding: '8px 12px', borderLeft: `4px solid ${s.rand}` }} data-testid="niveaustatus">
        <strong>{uitleg(status)}</strong>
        {status.goedgekeurd_op && <> · goedgekeurd {tijd(status.goedgekeurd_op)}{status.goedgekeurd_door && ` door ${status.goedgekeurd_door}`}</>}
        {status.gemaakt_op && status.status === 'concept' && <> · concept van {tijd(status.gemaakt_op)}</>}
      </div>

      {melding && <div className="alert alert-success mb-12">{melding}</div>}

      <div className="flex gap-8 mb-12" style={{ flexWrap: 'wrap' }}>
        {bewerken ? (
          <>
            <button className="btn btn-success" onClick={bewaar} disabled={bezig}>
              {bezig ? <Draaier /> : <Save size={16} />} Bewaar als concept
            </button>
            <button className="btn btn-secondary" onClick={() => setBewerken(false)} disabled={bezig}><X size={16} /> Annuleren</button>
          </>
        ) : (
          <>
            {versie?.concept && !isBezig && (
              <button className="btn btn-success" onClick={keurGoed} disabled={bezig}><Check size={16} /> Goedkeuren</button>
            )}
            {(versie?.concept || versie?.goedgekeurd) && !isBezig && (
              <button className="btn btn-primary" onClick={beginBewerken} disabled={bezig || laden || !versie?.is_versie}
                title={versie?.welke === 'goedgekeurd' && versie?.concept
                  ? 'Begint een nieuw concept bij de goedgekeurde versie; het concept dat er nu ligt, gaat dan weg' : undefined}>
                <Edit3 size={16} /> Bewerken
              </button>
            )}
            <button className="btn btn-warning" onClick={maakOpnieuw} disabled={bezig || isBezig}>
              {isBezig ? <Draaier /> : <RefreshCw size={16} />}
              {isBezig ? ' Wordt gemaakt...' : status.goedgekeurd || versie?.concept ? ' Opnieuw maken' : ' Maken'}
            </button>
            {versie?.concept && !isBezig && (
              <button className="btn btn-danger" onClick={gooiWeg} disabled={bezig}><Trash2 size={16} /> Concept weggooien</button>
            )}
            {versie?.concept && versie?.goedgekeurd && (
              <select className="input" style={{ width: 'auto', padding: '4px 8px', fontSize: 13 }} value={versie.welke}
                onChange={(e) => { setWelke(e.target.value); laad(e.target.value); }} aria-label="Welke versie">
                <option value="concept">Het concept</option>
                <option value="goedgekeurd">De goedgekeurde versie</option>
              </select>
            )}
          </>
        )}
      </div>

      {laden ? (
        <Laden tekst="Tekst laden..." />
      ) : versie && (
        <>
          <div className="card mb-8" style={{ background: 'var(--vlak)', padding: '8px 12px' }}>
            <small>
              {versie.is_versie
                ? <>{versie.welke === 'concept' ? 'Concept' : 'Goedgekeurde versie'} | </>
                : <>Nog geen tekst op dit niveau; hieronder de standaardtekst | </>}
              Lengte bij dit niveau: {versie.lengte} woorden
              {versie.gemaakt_met && versie.gemaakt_met !== versie.lengte && (
                <strong style={{ color: 'var(--waarschuw-tekst)' }}> (deze versie is gemaakt met {versie.gemaakt_met})</strong>
              )} | Werkelijk: <strong>{versie.woorden}</strong> woorden
            </small>
            {versie.is_versie && <TekstMaten maten={versie.maten} niveau={niveau} />}
            {versie.is_versie && versie.verloop && <Verloop verloop={versie.verloop} />}
            {!bewerken && heeftMarkeringen(versie.paragraphs) && (
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, fontSize: 13 }}>
                <input type="checkbox" checked={bouwstenen} onChange={e => setBouwstenen(e.target.checked)} />
                Bouwstenen tonen
              </label>
            )}
          </div>
          <div style={{ maxHeight: 480, overflow: 'auto', border: '1px solid var(--lijn-2)', padding: 16, borderRadius: 4 }}>
            {bewerken ? alineas.map((p, i) => (
              <textarea key={p.id} value={p.inhoud} aria-label={`Alinea ${i + 1}`}
                onChange={(e) => setAlineas(prev => prev.map((a, j) => (j === i ? { ...a, inhoud: e.target.value } : a)))}
                style={{ width: '100%', minHeight: 120, marginBottom: 16, padding: 12, border: '1px solid var(--lijn-3)',
                  borderRadius: 4, fontSize: 14, lineHeight: 1.7, fontFamily: 'inherit', resize: 'vertical' }} />
            )) : versie.paragraphs.map(p => (
              <p key={p.id} style={{ marginBottom: 16, lineHeight: 1.7, color: versie.is_versie ? 'inherit' : 'var(--tekst-3)' }}>
                {bouwstenen && p.gemarkeerd ? <GemarkeerdeTekst tekst={zonderKopjes(p.gemarkeerd)} /> : zonderKopjes(p.inhoud)}
              </p>
            ))}
          </div>
          {bewerken && heeftMarkeringen(versie.paragraphs) && (
            <p className="text-muted mt-8" style={{ fontSize: 13 }}>
              Let op: na een bewerking met de hand weet de app in die alinea niet meer waar de bouwstenen staan.
            </p>
          )}
        </>
      )}
    </Venster>
  );
}
